import {
  ActionButton,
  IComboBoxOption,
  ISearchBoxProps,
  SearchBox,
  Stack,
} from "@fluentui/react";
import { styles } from "./filterComponent.styles";
import ComboBoxFilter from "./ComboBoxFilter";
import DateRangeFilter from "./DateRangeFilter";
import useAppInsights from "../../Hooks/UseAppInsights";
import { OperationEventProperties } from "../AppInsights";
import DurationFilter from "./DurationFilter";

interface FilterComponentProps {
  searchOptions?: SearchOptions;
  filters?: Filter[];
  onClearFilters?: () => void;
}

interface SearchOptions extends ISearchBoxProps {}

interface Filter {
  key: string;
  text: string;
  type: FilterType;
  [key: string]: any;
}

export enum FilterTypes {
  Combobox = "combobox",
  DateRange = "daterange",
  DateTimeOperator = "datetimeoperator",
  Duration = "duration",
}

export type FilterType = FilterTypes;

export enum FilterAction {
  Clear = "clear",
}

const FilterComponent: React.FC<FilterComponentProps> = ({
  searchOptions,
  filters,
  onClearFilters,
}) => {
  const { trackOperationEvent } = useAppInsights("FilterComponent");

  return (
    <Stack
      horizontal
      verticalAlign="center"
      tokens={{ childrenGap: 8 }}
      className={styles.container}
    >
      <Stack.Item grow>
        <SearchBox
          className={styles.searchBox}
          placeholder={"Search"}
          {...searchOptions}
        />
      </Stack.Item>
      <Stack horizontal tokens={{ childrenGap: 8 }}>
        {filters &&
          filters.map((filter) =>
            getFilterByType(trackOperationEvent)(filter.type, filter),
          )}
      </Stack>
      {onClearFilters && (
        <ActionButton onClick={onClearFilters}>Clear filters</ActionButton>
      )}
    </Stack>
  );
};

const getFilterByType =
  (traceEventCallback: (properties: OperationEventProperties) => void) =>
  (type: FilterType, filter: Filter) => {
    switch (type) {
      case FilterTypes.Combobox:
        return (
          <ComboBoxFilter
            {...filter}
            filterKey={filter.key}
            text={filter.text}
            includeSelectAll={filter.includeSelectAll}
            options={filter.options}
            selectedKey={filter.selectedKey}
            onChange={(_: any, option: IComboBoxOption | undefined) => {
              filter.onChange(_, option);
              traceEventCallback({
                function: type,
                operation: "Filter",
                properties: {
                  filterKey: filter.key,
                  selectedKey: option ? option.key : null,
                },
              });
            }}
          />
        );
      case FilterTypes.DateRange:
        return (
          <DateRangeFilter
            {...filter}
            key={filter.key}
            text={filter.text}
            clearable={filter.clearable}
            selectedStartDate={filter.selectedStartDate}
            selectedEndDate={filter.selectedEndDate}
            onChange={(startDate?: Date, endDate?: Date) => {
              filter.onChange(startDate, endDate);
              traceEventCallback({
                function: type,
                operation: "Filter",
                properties: {
                  filterKey: filter.key,
                  selectedStartDate: startDate ? startDate.toISOString() : null,
                  selectedEndDate: endDate ? endDate.toISOString() : null,
                },
              });
            }}
          />
        );
      case FilterTypes.DateTimeOperator:
        return (
          <DateRangeFilter
            {...filter}
            key={filter.key}
            text={filter.text}
            clearable={filter.clearable}
            selectedDate={filter.selectedDate}
            selectedOperator={filter.selectedOperator}
            onChange={(date?: Date, operator?: string) => {
              filter.onChange(date, operator);
              traceEventCallback({
                function: type,
                operation: "Filter",
                properties: {
                  filterKey: filter.key,
                  selectedDate: date ? date.toISOString() : null,
                  selectedOperator: operator,
                },
              });
            }}
          />
        );
      case FilterTypes.Duration:
        return (
          <DurationFilter
            {...filter}
            key={filter.key}
            text={filter.text}
            onChange={(duration, increment, operator) => {
              filter.onChange(duration, increment, operator);
              traceEventCallback({
                function: type,
                operation: "Filter",
                properties: {
                  filterKey: filter.key,
                  duration: duration ?? "clear",
                  increment,
                  selectedOperator: operator,
                },
              });
            }}
          />
        );
      default:
        return null;
    }
  };

export const onComboFilterChange = (
  key: string,
  option: IComboBoxOption | undefined,
  setFilters: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>,
) => {
  if (option) {
    if (option.key === FilterAction.Clear) {
      setFilters((prev) => {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      });
    } else {
      setFilters((prev) => {
        const current = prev[key] || [];
        if (option.selected) {
          return {
            ...prev,
            [key]: [...current, option.key as string],
          };
        }
        const nextValues = current.filter((value) => value !== option.key);
        if (nextValues.length === 0) {
          const { [key]: _removed, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [key]: nextValues,
        };
      });
    }
  }
};

export default FilterComponent;
